import clsx from "clsx";
import React, { useCallback, useState } from "react";
import { LeadLink } from "web/react/components/lead-link";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { SizePicker } from "web/react/components/size-picker/size-picker";
import { ProductCardHorizontal } from "web/react/emo/product-card-horizontal";
import {
    EventLabel,
    PinSizeInfo,
    useSaveForLater,
} from "web/react/hooks/use-save-for-later/use-save-for-later";
import {
    baseAffiliateAnalyticsConfig,
    sendAnalyticsEvent,
} from "web/react/pages/product/out-of-stock/out-of-stock-product-page/utils";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import {
    ProductBuyOptionsSerializer,
    ProductLayoutSerializer,
    ProductPageAreaSerializer,
    ProductScreenSize,
} from "web/types/serializers";
import * as styles from "./product-area.css";
import { SeeMoreButton } from "./see-more-button/see-more-button";

export interface ProductAreaProps {
    productArea: ProductPageAreaSerializer;
    productLayout: ProductLayoutSerializer;
    uniqueRetailerCount: number;
    isCheckout: boolean;
    productDetailsContainerRef: React.RefObject<HTMLDivElement>;
    mainBuyOption: ProductBuyOptionsSerializer;
}

export function ProductArea({
    productArea,
    productLayout,
    uniqueRetailerCount,
    isCheckout,
    productDetailsContainerRef,
    mainBuyOption,
}: ProductAreaProps): React.ReactElement {
    const productId = productArea.product.product_id;
    const isUserLoggedIn = userProfiler.isLoggedIn();
    const productIsSavedButtonText =
        uniqueRetailerCount > 1
            ? gettext("oos_product.tracking_mutiple_retailers_notification", {
                  retailer_count: uniqueRetailerCount,
              })
            : gettext("oos_product.tracking_single_retailer_notification");

    const sizes = productArea.sizes.all_sizes;
    const [selectedSize, setSelectedSize] = useState<ProductScreenSize | undefined>(undefined);
    const [isSizePickerExpanded, setSizePickerExpanded] = useState<boolean>(false);
    const schemas = productArea.sizes.schema_labels;
    const userDefaultSchema = productArea.sizes.user_default_schema;
    const hasSizes = sizes.length > 0 && sizes.every((size) => size.localized_sizes);
    const { isSaved: productIsSaved, toggleSaveForLater } = useSaveForLater(
        productArea.product.product_id,
        EventLabel.OOS,
        undefined,
        undefined
    );

    const baseAnalytics = baseAffiliateAnalyticsConfig(mainBuyOption, productId);

    const convertSizeToPinInfo = useCallback((size: ProductScreenSize): PinSizeInfo[] | null => {
        if (!size.localized_sizes || !size.user_default_schema) {
            return null;
        }
        const localizedSize = size.localized_sizes[size.user_default_schema];

        if (!localizedSize || !localizedSize.tag) {
            return null;
        }

        // The tag might be a JSON string like:
        // '{"first_dimension": "size.jeans.au.w.16", "second_dimension": "size.jeans.inseam.inch.30"}'
        const sizeTag = localizedSize.tag;
        let pinSizes: PinSizeInfo[] = [];
        if (sizeTag.startsWith("{")) {
            try {
                const parsedTag = JSON.parse(sizeTag);
                pinSizes = Object.values(parsedTag).map((dimensionTag: any) => ({
                    size_tag: dimensionTag,
                }));
            } catch (e) {
                console.warn(
                    `Failed to parse multi-dimensional size tag: ${sizeTag}, threw error ${e}`
                );
            }
        } else {
            pinSizes.push({ size_tag: sizeTag }); // tag: "size.dress.uk.10"
        }
        return pinSizes;
    }, []);

    const handleSizePickerSelect = useCallback(
        (option: any) => {
            const size = option.value as ProductScreenSize;
            const sizeInfo = convertSizeToPinInfo(size);
            setSelectedSize(size);
            setSizePickerExpanded(false);

            if (sizeInfo) {
                toggleSaveForLater(sizeInfo);
            }
        },
        [convertSizeToPinInfo, toggleSaveForLater]
    );

    const trackCheckoutClick = useCallback(
        (label: string) => {
            if (isCheckout) {
                sendAnalyticsEvent("oos-product-hero", "click", label, {
                    buy_option_id: mainBuyOption.link_id.toString(),
                    product_id: productId,
                    retailer_slug: mainBuyOption.retailer_slug,
                });
            }
        },
        [isCheckout, mainBuyOption, productId]
    );

    const trackCheckoutClickAndScroll = useCallback(
        (label: string) => {
            trackCheckoutClick(label);
            productDetailsContainerRef.current?.scrollIntoView({ behavior: "smooth" });
        },
        [trackCheckoutClick, productDetailsContainerRef]
    );

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent, label: string): void => {
            if (["Space", "Enter"].includes(e.key)) {
                e.preventDefault();
                trackCheckoutClickAndScroll(label);
            }
        },
        [trackCheckoutClickAndScroll]
    );

    const ImageContainer = useCallback(
        ({ children }: { children: React.ReactNode }): React.ReactElement => {
            return isCheckout ? (
                <button
                    onClick={() => trackCheckoutClickAndScroll("checkout-gallery-image")}
                    data-testid="product-image"
                    className={styles.cursorPointer}
                    onKeyDown={(e) => onKeyDown(e, "checkout-gallery-image")}
                >
                    {children}
                </button>
            ) : (
                <LeadLink
                    {...baseAnalytics}
                    openInNewTab
                    reason="affiliate-gallery-image"
                    eventLabel="affiliate-gallery-image"
                    dataTestId="product-image"
                >
                    {children}
                </LeadLink>
            );
        },
        [isCheckout, trackCheckoutClickAndScroll, baseAnalytics, onKeyDown]
    );

    const ViewProductDetailsContainer = useCallback(
        ({ children }: { children: React.ReactNode }): React.ReactElement => {
            return isCheckout ? (
                <button
                    onClick={() => trackCheckoutClickAndScroll("checkout-view-product-details")}
                    data-testid="view-product-details"
                    className={clsx(styles.semanticButton, styles.cursorPointer)}
                    onKeyDown={(e) => onKeyDown(e, "checkout-view-product-details")}
                >
                    {children}
                </button>
            ) : (
                <LeadLink
                    {...baseAnalytics}
                    openInNewTab
                    reason="affiliate-view-product-details"
                    eventLabel="affiliate-view-product-details"
                    dataTestId="view-product-details"
                >
                    {children}
                </LeadLink>
            );
        },
        [isCheckout, baseAnalytics, trackCheckoutClickAndScroll, onKeyDown]
    );

    return (
        <div className={styles.productAreaContainer}>
            <ProductCardHorizontal
                product={productArea.product}
                designer={productArea.designer}
                buyOption={productArea.buy_options[0]}
                ImageContainer={ImageContainer}
                ViewProductDetailsContainer={ViewProductDetailsContainer}
            >
                {uniqueRetailerCount > 0 && (
                    <SaveForLaterButton
                        type="cta"
                        cta={{
                            title: gettext("oos_product.get_notified_when_back_in_stock_btn"),
                        }}
                        initialIsSaved={productIsSaved}
                        activeCta={{
                            title: productIsSavedButtonText,
                            icon: "notification-active",
                            iconPosition: "right",
                        }}
                        {...(isUserLoggedIn && { onClick: toggleSaveForLater })}
                        pageSource="oos"
                        productId={productId}
                        isLoggedIn={isUserLoggedIn}
                        size="lg"
                        variant="primary"
                        width="full"
                        beforeOnClick={() =>
                            sendAnalyticsEvent("oos-notification-alert", "clicked", "pdp_button", {
                                product_id: productId,
                            })
                        }
                    />
                )}
                {hasSizes && productIsSaved && (
                    <div style={{ marginTop: "5px" }}>
                        <SizePicker
                            onSelect={handleSizePickerSelect}
                            isExpanded={isSizePickerExpanded}
                            schemas={schemas}
                            defaultSelectText={gettext(
                                "oos_product.select_size_for_personalized_alerts"
                            )}
                            userDefaultSchema={userDefaultSchema}
                            setExpanded={(expanded: boolean): void =>
                                setSizePickerExpanded(expanded)
                            }
                            sizes={sizes}
                            productType={productArea.product_purchase_type}
                            selectedOption={selectedSize}
                            isExpressCheckout={false}
                            isComparison={false}
                            showDisabledOptions={true}
                            showAllSchemas={true}
                            selectedTextPrefix={
                                selectedSize
                                    ? ` ${gettext("oos_product.alert_set_for_size")} `
                                    : undefined
                            }
                        />
                    </div>
                )}
                <SeeMoreButton
                    productArea={productArea}
                    productLayout={productLayout}
                    mainBuyOption={mainBuyOption}
                    isCheckout={isCheckout}
                    trackCheckoutClick={trackCheckoutClick}
                />
            </ProductCardHorizontal>
        </div>
    );
}
